import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDataSaver } from "@/context/DataSaverContext";
import AfricaMap from "@/components/AfricaMap";
import api from "@/lib/api";
import africaGeo from "@/data/africa.geo.json";

// Approx capital coordinates [lon, lat] per delegation code
const COORDS = {
  DZ: [3, 36.7], AO: [13.2, -8.8], BJ: [2.6, 6.5], BW: [25.9, -24.6], BF: [-1.5, 12.4],
  BI: [29.9, -3.4], CV: [-17.5, 15.9], CM: [11.5, 3.9], CF: [18.6, 4.4], TD: [15, 12.1],
  KM: [43.3, -11.7], CG: [15.3, -4.3], CD: [21.7, -4], CI: [-4.5, 6.8], DJ: [43.1, 11.6],
  EG: [31.2, 30], GQ: [10.5, 1.6], ER: [38.9, 15.3], SZ: [31.5, -26.5], ET: [39.5, 9],
  GA: [11.6, -0.4], GM: [-15.6, 13.5], GH: [-1, 7.9], GN: [-11.7, 10.4], GW: [-15, 12],
  KE: [37.9, 0.2], LS: [28.2, -29.6], LR: [-9.4, 6.4], LY: [17.2, 27], MG: [46.9, -19],
  MW: [34.3, -13.3], ML: [-4, 17.6], MR: [-10.9, 20.3], MU: [57.5, -20.2], MA: [-6.8, 31.8],
  MZ: [35.5, -18.7], NA: [17.1, -22.6], NE: [8.1, 17.6], NG: [8, 9.6], RW: [30.1, -1.9],
  ST: [6.6, 0.3], SN: [-14.5, 14.5], SC: [55.5, -4.6], SL: [-11.8, 8.5], SO: [46.2, 5.2],
  ZA: [24.7, -29], SS: [31.3, 6.9], SD: [30.2, 15.5], TZ: [34.9, -6.4], TG: [1, 8.6],
  TN: [9.5, 34], UG: [32.3, 1.4], ZM: [27.8, -13.1], ZW: [29.2, -19], KUSH: [33, 18.5], SOL: [45, 9.6],
};

// ISO2 (delegation code) -> ISO3 (GeoJSON id) for country highlighting
const ISO3 = {
  DZ: "DZA", AO: "AGO", BJ: "BEN", BW: "BWA", BF: "BFA", BI: "BDI", CV: "CPV", CM: "CMR", CF: "CAF",
  TD: "TCD", KM: "COM", CG: "COG", CD: "COD", CI: "CIV", DJ: "DJI", EG: "EGY", GQ: "GNQ", ER: "ERI",
  SZ: "SWZ", ET: "ETH", GA: "GAB", GM: "GMB", GH: "GHA", GN: "GIN", GW: "GNB", KE: "KEN", LS: "LSO",
  LR: "LBR", LY: "LBY", MG: "MDG", MW: "MWI", ML: "MLI", MR: "MRT", MU: "MUS", MA: "MAR", MZ: "MOZ",
  NA: "NAM", NE: "NER", NG: "NGA", RW: "RWA", ST: "STP", SN: "SEN", SC: "SYC", SL: "SLE", SO: "SOM",
  ZA: "ZAF", SS: "SSD", SD: "SDN", TZ: "TZA", TG: "TGO", TN: "TUN", UG: "UGA", ZM: "ZMB", ZW: "ZWE",
};

// Equirectangular projection into a 640x680 viewBox over Africa's bbox
const LON0 = -20, LON1 = 60, LAT0 = 40, LAT1 = -37, W = 640, H = 680;
const px = (lon) => ((lon - LON0) / (LON1 - LON0)) * W;
const py = (lat) => ((LAT0 - lat) / (LAT0 - LAT1)) * H;

const ringToPath = (ring) => "M" + ring.map(([lon, lat]) => `${px(lon).toFixed(1)},${py(lat).toFixed(1)}`).join("L") + "Z";
const featurePath = (geom) => {
  const polys = geom.type === "MultiPolygon" ? geom.coordinates : [geom.coordinates];
  return polys.map((poly) => poly.map(ringToPath).join(" ")).join(" ");
};

export default function AfricaMapInteractive({ delegations = [] }) {
  const navigate = useNavigate();
  const { dataSaver } = useDataSaver();
  const [candMap, setCandMap] = useState({});
  const [hover, setHover] = useState(null);

  useEffect(() => {
    api.get("/candidates", { params: { limit: 100 } }).then(({ data }) => {
      const m = {};
      data.forEach((c) => { if (!m[c.delegation_code]) m[c.delegation_code] = c; });
      setCandMap(m);
    }).catch(() => {});
  }, []);

  const countries = useMemo(() => africaGeo.features.map((f) => ({ id: f.id, d: featurePath(f.geometry) })), []);
  const activeCountryIds = useMemo(() => {
    const s = new Set();
    delegations.forEach((d) => { if (candMap[d.code] && ISO3[d.code]) s.add(ISO3[d.code]); });
    return s;
  }, [delegations, candMap]);

  const pins = useMemo(() => delegations.filter((d) => COORDS[d.code]).map((d) => ({
    ...d, x: px(COORDS[d.code][0]), y: py(COORDS[d.code][1]), cand: candMap[d.code],
  })), [delegations, candMap]);

  // Graceful fallback for data-saver / low-end devices
  if (dataSaver) return <AfricaMap delegations={delegations} />;

  const active = hover && pins.find((p) => p.code === hover);
  const hoverIso3 = active && ISO3[active.code];

  return (
    <div className="relative" data-testid="africa-map-interactive">
      {/* Atmospheric backdrop */}
      <div className="pointer-events-none absolute inset-0 mesh-hero opacity-90" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-[#E6005C]/25 blur-[110px]" />

      <div className="relative mx-auto max-w-3xl [perspective:1400px]">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full [transform:rotateX(6deg)] drop-shadow-[0_40px_60px_rgba(0,0,0,0.65)]" role="img" aria-label="Interactive map of Africa">
          <defs>
            <radialGradient id="mapGlow" cx="50%" cy="42%" r="62%">
              <stop offset="0%" stopColor="rgba(230,0,92,0.30)" />
              <stop offset="100%" stopColor="rgba(230,0,92,0)" />
            </radialGradient>
            <linearGradient id="landFill" x1="0" y1="0" x2="0.6" y2="1">
              <stop offset="0%" stopColor="#241019" />
              <stop offset="100%" stopColor="#101015" />
            </linearGradient>
            <linearGradient id="landActive" x1="0" y1="0" x2="0.6" y2="1">
              <stop offset="0%" stopColor="rgba(230,0,92,0.42)" />
              <stop offset="100%" stopColor="rgba(153,0,61,0.20)" />
            </linearGradient>
            <filter id="pinGlow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="3.4" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          <ellipse cx={W / 2} cy={H / 2 - 24} rx={W / 2.05} ry={H / 2.05} fill="url(#mapGlow)" />

          {/* Real Africa country outlines */}
          <g strokeLinejoin="round">
            {countries.map((c) => {
              const isActive = activeCountryIds.has(c.id);
              const isHover = hoverIso3 === c.id;
              return (
                <path key={c.id} d={c.d}
                  fill={isHover ? "url(#landActive)" : isActive ? "rgba(230,0,92,0.14)" : "url(#landFill)"}
                  stroke={isHover ? "#FF4D94" : isActive ? "rgba(255,26,117,0.65)" : "rgba(230,0,92,0.28)"}
                  strokeWidth={isHover ? 1.6 : 0.7}
                  className="transition-all duration-300"
                  style={isHover ? { filter: "drop-shadow(0 0 10px rgba(255,77,148,0.6))" } : undefined} />
              );
            })}
          </g>

          {/* Delegation pins */}
          {pins.map((p) => {
            const on = hover === p.code;
            return (
              <g key={p.code} transform={`translate(${p.x},${p.y})`} className="cursor-pointer"
                 onClick={() => navigate(`/delegations/${p.code}`)}
                 onMouseEnter={() => setHover(p.code)} onMouseLeave={() => setHover(null)}>
                {p.cand && <circle r={on ? 15 : 11} fill="none" stroke="#FF1A75" strokeWidth="1.2" opacity={on ? 0.9 : 0.5} className="animate-ping" style={{ transformOrigin: "center" }} />}
                <circle r={on ? 13 : 9} fill={p.cand ? "rgba(230,0,92,0.22)" : "rgba(255,255,255,0.05)"} className="transition-all" />
                <circle r="4" fill={p.cand ? "#FF4D94" : "#6b7280"} filter={p.cand ? "url(#pinGlow)" : undefined} className={p.cand ? "animate-pulse" : ""} />
                <title>{p.name}</title>
              </g>
            );
          })}
        </svg>

        {active && (
          <button
            onClick={() => navigate(`/delegations/${active.code}`)}
            data-testid={`map-pin-card-${active.code}`}
            className="absolute z-10 w-52 -translate-x-1/2 rounded-xl glass-strong p-3 text-left shadow-2xl glow-pink"
            style={{ left: `${(active.x / W) * 100}%`, top: `calc(${(active.y / H) * 100}% + 14px)` }}
          >
            <div className="flex items-center gap-2">
              <span className="text-2xl">{active.flag}</span>
              <div className="min-w-0"><p className="truncate text-sm text-amber-50">{active.name}</p><p className="text-[0.6rem] uppercase tracking-wider text-muted-foreground">{active.region} Africa</p></div>
            </div>
            {active.cand ? (
              <div className="mt-2 flex items-center gap-2 border-t border-gold/15 pt-2">
                <img src={active.cand.portrait_url} alt="" className="h-9 w-9 rounded-full object-cover object-top" />
                <div className="min-w-0"><p className="truncate text-xs text-amber-50">{active.cand.name}</p><p className="text-[0.6rem] gold-text">{active.cand.votes.toLocaleString()} votes</p></div>
              </div>
            ) : (
              <p className="mt-2 border-t border-gold/15 pt-2 text-[0.65rem] text-muted-foreground">Candidate coming soon</p>
            )}
          </button>
        )}
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">Tap a glowing pin to explore a delegation. <span className="text-gold">Pink = candidate confirmed.</span></p>
    </div>
  );
}
